import * as React from 'react';
import { pure } from 'recompose';

/* UI */
import TextField from 'material-ui/TextField';

/* Utils */
import {
  formatPhoneNumber,
  removeNonNumericCharacters,
} from '../../../../utils/formattingHelpers';

/* Style */
import {
  textInputBox,
  textActiveInputBox,
  textErrorInputBox,
} from '../../../../styles/input.style';

/* Data */
import { TextInputProps, TextInputState, CustomKeyboardEvent } from '../types';

class PhoneInput extends React.Component<TextInputProps, TextInputState> {
  phoneRef = null as any;
  cursorPosition = null as number | null;

  constructor(props: TextInputProps) {
    super(props);
    this.state = {
      focused: false,
    };
  }

  componentDidUpdate(prevProps: TextInputProps) {
    // Move cursor if needed after the phone number dash gets updated.
    if (this.cursorPosition === null) return;
    this.setCursor(this.cursorPosition);
    this.cursorPosition = null;
  }

  setCursor = (position: number): void => {
    // Material-ui textfield doesnt accept selectionStart, though apparently may be fixed with next release:
    // https://github.com/mui-org/material-ui/issues/7550
    this.phoneRef.input.selectionStart = position;
    this.phoneRef.input.selectionEnd = position;
  };

  onKeyDown = (event: CustomKeyboardEvent): void => {
    // If the user hits backspace or delete on a dash "-", we should remove the next character.
    // Note(gemma): This KeyDown handler doesnt apply the key yet. That happens later in onInput:
    // onKeyDown > onKeyPress > onInput > onChange ...> onKeyUp
    // So, if we just move the cursor position here, it will erase the character instead of the dash.
    const keyValue = String.fromCharCode(event.keyCode);
    const key = event.key === 'Unidentified' ? keyValue : event.key;
    const { value, selectionStart, selectionEnd } = event.target;

    if (selectionStart !== selectionEnd || !value) return;

    if (key === 'Backspace' && selectionStart >= 1) {
      const indexOfCharToRemove = selectionStart - 1;
      if (value[indexOfCharToRemove] === '-') {
        this.setCursor(indexOfCharToRemove);
      }
    }
    if (key === 'Delete' && selectionStart <= value.length - 1) {
      const indexOfCharToRemove = selectionStart;
      if (value[indexOfCharToRemove] === '-') {
        this.setCursor(indexOfCharToRemove + 1);
      }
    }
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const { input } = this.props;

    // Don't allow a phone number with more than 11 digits (US international format).
    const digitsOnly = removeNonNumericCharacters(value);
    if (digitsOnly.length > 11) {
      return;
    }

    // Determine where the cursor should be, accounting for the formatting that happens in render.
    // Here we added or removed a digit and the cursor is where the dash would be.
    let cursorPosition = event.target.selectionStart || 0;
    if (
      digitsOnly.length > input.length &&
      (value.length === 14 ||
        cursorPosition === 4 ||
        cursorPosition === 8 ||
        cursorPosition === 13)
    ) {
      cursorPosition += 1;
    } else if (
      digitsOnly.length < input.length &&
      (input.length === 11 ||
        cursorPosition === 4 ||
        cursorPosition === 8 ||
        cursorPosition === 13) &&
      digitsOnly.length > 3 &&
      cursorPosition > 0
    ) {
      cursorPosition -= 1;
    }

    // Cache on the class so we can use in componentDidUpdate. The setState callback isnt good enough.
    this.cursorPosition = cursorPosition;
    this.props.onChange(this.props.id, digitsOnly);
  };

  onBlur = () => {
    this.setState({ focused: false });
    this.props.onBlur(this.props.id);
  };

  onFocus = () => {
    this.setState({ focused: true });
  };

  render() {
    const {
      input,
      id,
      name,
      showError,
      floatingLabelText,
      hintText,
      inputBoxStyle,
    } = this.props;
    const { focused } = this.state;
    const phoneNumber = formatPhoneNumber(input);

    let boxStyle;
    if (focused) {
      boxStyle = { ...textActiveInputBox };
    } else if (showError) {
      boxStyle = { ...textErrorInputBox };
    } else {
      boxStyle = { ...textInputBox };
    }

    if (inputBoxStyle) {
      boxStyle.style = { ...boxStyle.style, ...inputBoxStyle };
    }

    return (
      <TextField
        ref={ref => (this.phoneRef = ref)}
        id={id}
        name={name}
        type="tel"
        value={phoneNumber}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        onChange={this.onChange}
        onKeyDown={this.onKeyDown}
        floatingLabelText={floatingLabelText}
        hintText={hintText}
        fullWidth
        underlineShow={false}
        {...boxStyle}
      />
    );
  }
}

export default pure(PhoneInput);
/* Example use with TextInputValidator:
<TextInputValidator
  id={'cellphone'}
  name={'phone'}
  componentType={'PhoneInput'}
  floatingLabelText="Phone Number*"
  hintText="(___)-___-___"
  validators={['required']}
  showError={showError}
  errorAlign={'bottom'}
  errorEnum={errorEnum}
  errorEnum={''}
  input={input}
  onChange={this.onChange}
  onBlur={this.onBlur}
  onValidate={this.onValidate}
  inputBoxStyle={{ width: '100px' }}
/>
*/
